import { Popover } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickerDay } from '@mui/x-date-pickers/PickerDay';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import React, { useState } from 'react';

import i18n from '@/i18n';

import styles from './DateRangeField.module.scss';
import { Icon } from '@ui';

// Stile Design System per i TextField dei TimePicker (radius/focus come <Input/>).
const DS_TEXTFIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'var(--color-surface)',
    fontSize: 'var(--font-size-body)',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-input-border)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-primary-border)' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-teal)',
    borderWidth: '1px',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--color-teal)' },
};

// Combina la data di `dateObj` con l'orario di `base` (dayjs).
const withTimeOf = (base, dateObj) =>
  dateObj.hour(base.hour()).minute(base.minute()).second(0).millisecond(0);

// Cella giorno custom per il DateCalendar (MUI X free): estende `PickerDay`
// evidenziando l'intervallo start→end con uno sfondo continuo e gli estremi
// come cerchio pieno — la tecnica standard MUI per un range picker senza
// il pacchetto Pro (che offre DateRangeCalendar già pronto).
function makeRangeDay(start, end) {
  return function RangeDay({ day, outsideCurrentMonth, ...other }) {
    const isStart = start && day.isSame(start, 'day');
    const isEnd = end && day.isSame(end, 'day');
    const inRange = start && end && day.isAfter(start, 'day') && day.isBefore(end, 'day');
    return (
      <PickerDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={isStart || isEnd}
        sx={{
          ...(inRange && {
            backgroundColor: 'var(--color-teal-container)',
            borderRadius: 0,
          }),
          ...(isStart && { borderTopLeftRadius: '50%', borderBottomLeftRadius: '50%' }),
          ...(isEnd && { borderTopRightRadius: '50%', borderBottomRightRadius: '50%' }),
        }}
      />
    );
  };
}

/**
 * DateRangeField — campo unico "da → a" costruito sulle primitive free di
 * `@mui/x-date-pickers` (`DateCalendar` + `PickerDay` custom per l'highlight
 * del range): il vero `DateRangeCalendar` è nel pacchetto `-pro` a pagamento,
 * non installato nel progetto.
 *
 * Un trigger in stile Design System apre un popover con DUE `DateCalendar`
 * affiancati (mese di inizio / mese successivo): la prima selezione fissa
 * l'inizio, la seconda la fine (vincolata a `maxRangeDays` giorni), con
 * l'intervallo evidenziato in tempo reale su entrambi i calendari. Se la
 * seconda data è precedente all'inizio, riparte da quella come nuovo inizio.
 *
 * La selezione è gestita con uno stato interno (draft), sincronizzato da
 * `start`/`end` solo all'apertura: `onChange` viene chiamato dal genitore
 * una volta sola, a intervallo completo (2° click, o "Applica" in modalità
 * `withTime`) — mai su una data intermedia — così un consumer che aggancia
 * `onChange` a un fetch (es. Storico) non scarica dati finché l'utente non
 * ha scelto sia l'inizio che la fine.
 *
 * Props:
 * - `start`, `end`: dayjs (controllati dal caller)
 * - `maxRangeDays`: ampiezza massima dell'intervallo (default 7)
 * - `onChange(start, end)`: chiamato con i nuovi estremi dayjs, a selezione completata
 * - `label`: etichetta opzionale sopra il campo
 * - `withTime`: se true mostra anche i selettori di orario (inizio/fine) e
 *   richiede conferma esplicita ("Applica") invece di chiudersi al 2° click.
 */
export default function DateRangeField({
  start,
  end,
  maxRangeDays = 7,
  onChange,
  label,
  withTime = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [picking, setPicking] = useState('start'); // 'start' | 'end'
  // Estremi in corso di selezione: seguono i click dell'utente ma NON
  // propagano al genitore finché il range non è completo.
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);
  const open = Boolean(anchorEl);

  const fmt = withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
  const display = `${start.format(fmt)} → ${end.format(fmt)}`;

  const openPopover = (e) => {
    setPicking('start');
    setDraftStart(start);
    setDraftEnd(end);
    setAnchorEl(e.currentTarget);
  };

  const closePopover = () => setAnchorEl(null);

  const handleDay = (d) => {
    if (picking === 'start') {
      const nextStart = withTime ? withTimeOf(draftStart, d) : d.startOf('day');
      const maxEnd = nextStart.add(maxRangeDays - 1, 'day');
      const keepEnd = !draftEnd.isAfter(maxEnd) && !draftEnd.isBefore(nextStart);
      const nextEnd = keepEnd
        ? draftEnd
        : withTime
          ? withTimeOf(draftEnd, maxEnd)
          : maxEnd.endOf('day');
      setDraftStart(nextStart);
      setDraftEnd(nextEnd);
      setPicking('end');
      return;
    }

    if (d.isBefore(draftStart, 'day')) {
      // La data scelta è prima dell'inizio: diventa il nuovo inizio, il
      // vecchio inizio diventa la fine — selezione completa, si conferma.
      const nextStart = withTime ? withTimeOf(draftStart, d) : d.startOf('day');
      setDraftStart(nextStart);
      setDraftEnd(draftStart);
      onChange(nextStart, draftStart);
      if (!withTime) closePopover();
      return;
    }

    const maxEnd = draftStart.add(maxRangeDays - 1, 'day');
    const capped = d.isAfter(maxEnd, 'day') ? maxEnd : d;
    const nextEnd = withTime ? withTimeOf(draftEnd, capped) : capped.endOf('day');
    setDraftEnd(nextEnd);
    onChange(draftStart, nextEnd);
    if (!withTime) closePopover();
  };

  const handleTimeChange = (edge) => (t) => {
    if (!t) return;
    if (edge === 'start') setDraftStart(withTimeOf(t, draftStart));
    else setDraftEnd(withTimeOf(t, draftEnd));
  };

  const applyDraft = () => {
    onChange(draftStart, draftEnd);
    closePopover();
  };

  // Vincoli del calendario in base alla fase di selezione: al 1° click
  // qualunque data è ammessa (passata o futura); solo dal 2° click in poi si
  // vincola la finestra a `maxRangeDays` giorni dall'inizio scelto.
  const calMin = picking === 'end' ? draftStart : undefined;
  const calMax = picking === 'end' ? draftStart.add(maxRangeDays - 1, 'day') : undefined;
  const RangeDay = makeRangeDay(draftStart, draftEnd);
  const rightMonth = draftEnd.isSame(draftStart, 'month') ? draftStart.add(1, 'month') : draftEnd;

  return (
    <div className={styles.field}>
      {label && <span className={styles.label}>{label}</span>}
      <button type="button" className={styles.trigger} onClick={openPopover}>
        <Icon name="calendarDays" aria-hidden="true" />
        <span>{display}</span>
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className={styles.hint}>
          {picking === 'start'
            ? i18n.t('seleziona_inizio', { defaultValue: 'Seleziona la data di inizio' })
            : i18n.t('seleziona_fine', { defaultValue: 'Seleziona la data di fine' })}
        </div>

        <div className={styles.calendars}>
          <DateCalendar
            referenceDate={draftStart}
            value={null}
            minDate={calMin}
            maxDate={calMax}
            onChange={handleDay}
            slots={{ day: RangeDay }}
          />
          <DateCalendar
            referenceDate={rightMonth}
            value={null}
            minDate={calMin}
            maxDate={calMax}
            onChange={handleDay}
            slots={{ day: RangeDay }}
          />
        </div>

        {withTime && (
          <div className={styles.timeRow}>
            <TimePicker
              label={i18n.t('ora_inizio', { defaultValue: 'Ora inizio' })}
              value={draftStart}
              ampm={false}
              onChange={handleTimeChange('start')}
              slotProps={{ textField: { size: 'small', sx: DS_TEXTFIELD_SX } }}
            />
            <TimePicker
              label={i18n.t('ora_fine', { defaultValue: 'Ora fine' })}
              value={draftEnd}
              ampm={false}
              onChange={handleTimeChange('end')}
              slotProps={{ textField: { size: 'small', sx: DS_TEXTFIELD_SX } }}
            />
          </div>
        )}

        {withTime && (
          <div className={styles.footer}>
            <button type="button" className={styles.apply} onClick={applyDraft}>
              {i18n.t('applica', { defaultValue: 'Applica' })}
            </button>
          </div>
        )}
      </Popover>
    </div>
  );
}
